import { useCallback, useEffect, useState } from 'react';
import { FiX, FiPackage, FiExternalLink } from 'react-icons/fi';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { apiRequest } from '../utils/auth';

const BG_CARD = 'var(--color-surface-secondary)';
const BORDER = 'var(--color-border)';
const TEXT = 'var(--color-text)';
const TEXT_DIM = 'var(--color-muted)';
const PURPLE = '#a371f7';
const GREEN = 'var(--color-success)';
const BLUE = 'var(--color-accent)';
const ORANGE = 'var(--color-warning)';
const RED = 'var(--color-danger)';

interface LensBreakdownEntry { key: string; label: string; count: number }

interface LensStats {
  total: number;
  status: {
    public: number; private: number; published: number; drafts: number;
    suspended: number; inReview: number; deleted: number; archived: number;
    official: number; community: number;
  };
  activity: {
    createdLast7Days: number; createdLast30Days: number;
    creators: number; publishingCreators: number;
  };
  byImageModel: LensBreakdownEntry[];
  byCategory: LensBreakdownEntry[];
  byTier: LensBreakdownEntry[];
  byAspectRatio: LensBreakdownEntry[];
}

interface LensStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opens the Lenses page, so the modal can hand off to the full list. */
  onViewAll?: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: BG_CARD,
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  width: '100%',
  maxWidth: '880px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

function Total({ label, value, color, hint }: {
  label: string; value: number; color?: string; hint?: string;
}) {
  return (
    <div style={{ background: 'var(--color-bg)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ color: TEXT_DIM, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color: color || TEXT, fontSize: 20, fontWeight: 700, marginTop: 2 }}>
        {value.toLocaleString()}
      </div>
      {hint && <div style={{ color: TEXT_DIM, fontSize: 11 }}>{hint}</div>}
    </div>
  );
}

/**
 * A distribution as labelled bars. Shares are of `total` (every lens document),
 * except for array-valued axes — a lens sits in several categories and tiers at
 * once, so those slices sum past 100% and the share is left off.
 */
function Distribution({ title, rows, total, showShare, note }: {
  title: string;
  rows: LensBreakdownEntry[];
  total: number;
  showShare: boolean;
  note?: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <div style={{ background: 'var(--color-bg)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: note ? 2 : 10 }}>{title}</div>
      {note && <div style={{ color: TEXT_DIM, fontSize: 11, marginBottom: 10 }}>{note}</div>}
      {rows.length === 0 ? (
        <div style={{ color: TEXT_DIM, fontSize: 12 }}>No lenses yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((row) => (
            <div key={row.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, marginBottom: 3 }}>
                <span style={{ color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.label}>
                  {row.label}
                </span>
                <span style={{ color: TEXT_DIM, whiteSpace: 'nowrap' }}>
                  {row.count.toLocaleString()}
                  {showShare && total > 0 && ` · ${((row.count / total) * 100).toFixed(1)}%`}
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--color-surface-hover)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: max > 0 ? `${(row.count / max) * 100}%` : '0%',
                  height: '100%',
                  background: PURPLE,
                  borderRadius: 3,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LensStatsModal({ isOpen, onClose, onViewAll }: LensStatsModalProps) {
  const [stats, setStats] = useState<LensStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // apiRequest spreads the { success, data } envelope, so the payload's
      // fields arrive at the top level
      const response = await apiRequest<LensStats & { success: boolean }>('/api/admin/lens-stats');
      setStats(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lens statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchStats();
  }, [isOpen, fetchStats]);

  // Escape dismisses the modal, same as the × button and the backdrop click.
  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiPackage style={{ color: PURPLE, fontSize: 18 }} />
            <div>
              <h2 style={{ margin: 0, color: TEXT, fontSize: 17 }}>Lens Statistics</h2>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>
                {stats ? `${stats.total.toLocaleString()} lenses` : 'All lenses'} · every document, drafts and deleted included
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onViewAll && (
              <button
                type="button"
                onClick={onViewAll}
                style={{
                  background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
                  color: TEXT, fontSize: 12, padding: '6px 10px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                Lenses page <FiExternalLink />
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: 'none', border: 'none', color: TEXT_DIM, fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
            >
              <FiX />
            </button>
          </div>
        </div>

        <div style={{ padding: '18px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: TEXT_DIM }}>Loading…</div>
          ) : error ? (
            <div style={{ color: RED, background: 'var(--color-surface)', border: `1px solid ${RED}`, borderRadius: 8, padding: '14px' }}>
              {error}
            </div>
          ) : stats ? (
            <>
              <div>
                <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Composition</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                  <Total label="Total" value={stats.total} />
                  <Total label="Public" value={stats.status.public} color={GREEN}
                    hint={stats.total > 0 ? `${((stats.status.public / stats.total) * 100).toFixed(1)}% of all` : undefined} />
                  <Total label="Private" value={stats.status.private} color={TEXT_DIM} />
                  <Total label="Published" value={stats.status.published} color={BLUE} />
                  <Total label="Drafts" value={stats.status.drafts} color={TEXT_DIM} />
                  <Total label="Official" value={stats.status.official} color={BLUE} />
                  <Total label="Community" value={stats.status.community} color={PURPLE} />
                  <Total label="Suspended" value={stats.status.suspended} color={RED} />
                  <Total label="In review" value={stats.status.inReview} color={ORANGE} />
                  <Total label="Archived" value={stats.status.archived} color={TEXT_DIM} />
                  <Total label="Deleted" value={stats.status.deleted} color={RED} />
                </div>
              </div>

              <div>
                <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Creators & recent activity</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                  <Total label="Creators" value={stats.activity.creators} hint="with at least one lens" />
                  <Total label="Publishing creators" value={stats.activity.publishingCreators} color={GREEN} hint="live public lens" />
                  <Total label="New (7d)" value={stats.activity.createdLast7Days} color={BLUE} />
                  <Total label="New (30d)" value={stats.activity.createdLast30Days} color={BLUE} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>
                <Distribution
                  title="By image model"
                  rows={stats.byImageModel || []}
                  total={stats.total}
                  showShare
                />
                <Distribution
                  title="By category"
                  rows={stats.byCategory || []}
                  total={stats.total}
                  showShare={false}
                  note="A lens can sit in several categories, so these do not sum to the total."
                />
                <Distribution
                  title="By access tier"
                  rows={stats.byTier || []}
                  total={stats.total}
                  showShare={false}
                  note="A lens can be available to several tiers at once."
                />
                <Distribution
                  title="By aspect ratio"
                  rows={stats.byAspectRatio || []}
                  total={stats.total}
                  showShare
                />
              </div>

              <div style={{ color: TEXT_DIM, fontSize: 11, lineHeight: 1.6, borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
                Counts cover every lens document — drafts, archived and soft-deleted included — so they
                add up to the Total Lenses figure on the dashboard. Model and category identifiers are
                resolved to their display names; one shown as a raw id points at an entry that has since
                been removed or renamed. Long tails are rolled into an “Others” row.
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
